import React, { useState, useEffect, useCallback } from 'react';
import client from '../api/client';
import FinTable from "../components/FinTable";

/* ── palette (matches app theme) ─────────────────────────────────── */
const C = {
  pageBg:   '#0a0f1a',
  cardBg:   '#0d1321',
  border:   '#1e2a3a',
  text:     '#e2e8f0',
  muted:    '#6b7a90',
  accent:   '#3b82f6',
  green:    '#10b981',
  red:      '#ef4444',
  orange:   '#f59e0b',
  purple:   '#a855f7',
  cyan:     '#06b6d4',
  pink:     '#ec4899',
};

/* action → color + label */
const ACTION_STYLE = {
  Buy:     { bg: '#064e3b', color: C.green,  label: 'Buy' },
  Sell:    { bg: '#4c0519', color: C.red,    label: 'Sell' },
  Convert: { bg: '#3b0764', color: C.purple, label: 'Convert' },
  Coupon:  { bg: '#78350f', color: C.orange, label: 'Coupon' },
  Short:   { bg: '#7f1d1d', color: C.red,    label: 'Short' },
  Cover:   { bg: '#064e3b', color: C.green,  label: 'Cover' },
};

const fmt = (v, dec = 0) => {
  if (v == null || v === '') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
};

const fmtUSD = (v) => {
  if (v == null || v === '') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/* ── styles ──────────────────────────────────────────────────────── */
const s = {
  page: { padding: '24px 32px', color: C.text, background: C.pageBg, minHeight: '100vh' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, color: C.text, margin: 0 },
  subtitle: { fontSize: 13, color: C.muted, marginTop: 4 },

  /* KPI row */
  kpiRow: { display: 'flex', gap: 12, marginBottom: 20 },
  kpiCard: {
    flex: 1, padding: '14px 18px', borderRadius: 8,
    background: C.cardBg, border: `1px solid ${C.border}`,
  },
  kpiLabel: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: C.muted },
  kpiValue: { fontSize: 20, fontWeight: 700, marginTop: 4 },

  /* Filters */
  filterRow: {
    display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center',
  },
  filterPill: {
    padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', border: '1px solid', transition: 'all 0.15s',
  },
  pillActive: { background: C.accent, borderColor: C.accent, color: '#fff' },
  pillInactive: { background: 'transparent', borderColor: C.border, color: C.muted },
  filterInput: {
    padding: '6px 12px', borderRadius: 6, fontSize: 12, border: `1px solid ${C.border}`,
    background: C.cardBg, color: C.text, outline: 'none', minWidth: 130,
  },
  filterLabel: { fontSize: 11, color: C.muted, fontWeight: 600, marginLeft: 8, marginRight: 4 },

  /* Table */
  tableWrap: {
    background: C.cardBg, borderRadius: 10, border: `1px solid ${C.border}`,
    overflow: 'hidden',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.5px', color: C.muted,
    borderBottom: `1px solid ${C.border}`, background: '#0b1120',
  },
  thRight: { textAlign: 'right' },
  td: { padding: '10px 14px', borderBottom: `1px solid ${C.border}22` },
  tdRight: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  rowHover: { background: '#131d30' },

  /* Action badge */
  badge: {
    display: 'inline-block', padding: '3px 10px', borderRadius: 12,
    fontSize: 11, fontWeight: 700, letterSpacing: '0.3px',
  },

  /* Status badge */
  statusBadge: {
    display: 'inline-block', padding: '2px 8px', borderRadius: 10,
    fontSize: 10, fontWeight: 700,
  },

  /* Empty */
  empty: { padding: 40, textAlign: 'center', color: C.muted, fontSize: 14 },

  /* Export */
  exportBtn: {
    padding: '7px 16px', borderRadius: 6, border: `1px solid ${C.border}`,
    background: 'transparent', color: C.muted, fontSize: 12, fontWeight: 600,
    cursor: 'pointer',
  },
};

/* ── component ───────────────────────────────────────────────────── */
export default function TradeBlotter() {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);

  /* Filters */
  const [actionFilter, setActionFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchTicker, setSearchTicker] = useState('');
  const [searchDeal, setSearchDeal] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchLots = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await client.get('/positions/tax-lots/');
      const data = resp.data.results || resp.data;
      setLots(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLots(); }, [fetchLots]);

  /* ── Filtering ── */
  const filtered = lots.filter(lot => {
    if (actionFilter !== 'All' && lot.action !== actionFilter) return false;
    if (statusFilter !== 'All') {
      const open = lot.is_open;
      if (statusFilter === 'Open' && !open) return false;
      if (statusFilter === 'Closed' && open) return false;
    }
    if (searchTicker && !(lot.security_ticker || '').toLowerCase().includes(searchTicker.toLowerCase())) return false;
    if (searchDeal && !(lot.deal_name || '').toLowerCase().includes(searchDeal.toLowerCase())) return false;
    if (dateFrom && lot.trade_date < dateFrom) return false;
    if (dateTo && lot.trade_date > dateTo) return false;
    return true;
  });

  /* Sort by date descending */
  const sorted = [...filtered].sort((a, b) => b.trade_date.localeCompare(a.trade_date));

  /* ── KPIs ── */
  const actionCounts = {};
  lots.forEach(l => { actionCounts[l.action] = (actionCounts[l.action] || 0) + 1; });
  const uniqueActions = [...new Set(lots.map(l => l.action))].sort();
  const totalCostBasis = lots.filter(l => l.is_open).reduce((sum, l) => sum + parseFloat(l.cost_basis || 0), 0);
  const openCount = lots.filter(l => l.is_open).length;
  const closedCount = lots.filter(l => !l.is_open).length;

  /* ── Export CSV ── */
  const exportCSV = () => {
    const headers = ['Date', 'Action', 'Security', 'Deal', 'Qty', 'Cost Basis', 'Unit Cost', 'P&L', 'Status'];
    const rows = sorted.map(l => [
      l.trade_date, l.action, l.security_ticker, l.deal_name || '',
      l.quantity, l.cost_basis, l.unit_cost,
      l.realized_pnl || '', l.is_open ? 'Open' : 'Closed',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'trade_blotter_' + new Date().toISOString().slice(0, 10) + '.csv';
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Trade Blotter</h1>
          <p style={s.subtitle}>All trade and conversion activity — chronological audit trail</p>
        </div>
        <button style={s.exportBtn} onClick={exportCSV}>📥 Export CSV</button>
      </div>

      {/* KPI Row */}
      <div style={s.kpiRow}>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Total Lots</div>
          <div style={{ ...s.kpiValue, color: C.accent }}>{lots.length}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Open</div>
          <div style={{ ...s.kpiValue, color: C.green }}>{openCount}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Closed</div>
          <div style={{ ...s.kpiValue, color: C.red }}>{closedCount}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Open Cost Basis</div>
          <div style={{ ...s.kpiValue, color: C.orange }}>{fmtUSD(totalCostBasis)}</div>
        </div>
        {uniqueActions.map(action => (
          <div style={s.kpiCard} key={action}>
            <div style={s.kpiLabel}>{action}s</div>
            <div style={{ ...s.kpiValue, color: (ACTION_STYLE[action] || {}).color || C.text }}>
              {actionCounts[action]}
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={s.filterRow}>
        {/* Action pills */}
        {['All', ...uniqueActions].map(a => (
          <span
            key={a}
            style={{ ...s.filterPill, ...(actionFilter === a ? s.pillActive : s.pillInactive) }}
            onClick={() => setActionFilter(a)}
          >
            {a}
          </span>
        ))}

        <span style={{ width: 1, height: 24, background: C.border, margin: '0 4px' }} />

        {/* Status pills */}
        {['All', 'Open', 'Closed'].map(st => (
          <span
            key={st}
            style={{ ...s.filterPill, ...(statusFilter === st ? s.pillActive : s.pillInactive) }}
            onClick={() => setStatusFilter(st)}
          >
            {st}
          </span>
        ))}

        <span style={{ width: 1, height: 24, background: C.border, margin: '0 4px' }} />

        {/* Text filters */}
        <span style={s.filterLabel}>Ticker:</span>
        <input style={s.filterInput} placeholder="e.g. TEST-COM" value={searchTicker} onChange={e => setSearchTicker(e.target.value)} />
        <span style={s.filterLabel}>Deal:</span>
        <input style={s.filterInput} placeholder="e.g. Corp" value={searchDeal} onChange={e => setSearchDeal(e.target.value)} />
        <span style={s.filterLabel}>From:</span>
        <input type="date" style={s.filterInput} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        <span style={s.filterLabel}>To:</span>
        <input type="date" style={s.filterInput} value={dateTo} onChange={e => setDateTo(e.target.value)} />
      </div>

      {/* Results count */}
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
        Showing {sorted.length} of {lots.length} lots
        {(actionFilter !== 'All' || statusFilter !== 'All' || searchTicker || searchDeal || dateFrom || dateTo) && (
          <span
            style={{ marginLeft: 10, color: C.accent, cursor: 'pointer', fontWeight: 600 }}
            onClick={() => { setActionFilter('All'); setStatusFilter('All'); setSearchTicker(''); setSearchDeal(''); setDateFrom(''); setDateTo(''); }}
          >
            ✕ Clear filters
          </span>
        )}
      </div>

      {/* Table */}
      <div style={s.tableWrap}>
        {loading ? (
          <div style={s.empty}>Loading trade activity...</div>
        ) : error ? (
          <div style={{ ...s.empty, color: C.red }}>{error}</div>
        ) : sorted.length === 0 ? (
          <div style={s.empty}>No trade activity found</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Date</th>
                <th style={s.th}>Action</th>
                <th style={s.th}>Security</th>
                <th style={s.th}>Deal</th>
                <th style={{ ...s.th, ...s.thRight }}>Quantity</th>
                <th style={{ ...s.th, ...s.thRight }}>Cost Basis</th>
                <th style={{ ...s.th, ...s.thRight }}>Unit Cost</th>
                <th style={{ ...s.th, ...s.thRight }}>P&L</th>
                <th style={s.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(lot => {
                const as = ACTION_STYLE[lot.action] || { bg: C.border, color: C.text, label: lot.action };
                const pnl = parseFloat(lot.realized_pnl || 0);
                return (
                  <tr
                    key={lot.id}
                    style={hoveredRow === lot.id ? s.rowHover : {}}
                    onMouseEnter={() => setHoveredRow(lot.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <td style={s.td}>{lot.trade_date}</td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, background: as.bg, color: as.color }}>
                        {as.label}
                      </span>
                    </td>
                    <td style={{ ...s.td, fontWeight: 600 }}>{lot.security_ticker}</td>
                    <td style={{ ...s.td, color: C.muted, fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {lot.deal_name || '—'}
                    </td>
                    <td style={{ ...s.td, ...s.tdRight }}>{fmt(lot.quantity)}</td>
                    <td style={{ ...s.td, ...s.tdRight, color: C.green }}>{fmtUSD(lot.cost_basis)}</td>
                    <td style={{ ...s.td, ...s.tdRight }}>${fmt(lot.unit_cost, 4)}</td>
                    <td style={{ ...s.td, ...s.tdRight, color: pnl > 0 ? C.green : pnl < 0 ? C.red : C.muted }}>
                      {lot.realized_pnl ? fmtUSD(lot.realized_pnl) : '—'}
                    </td>
                    <td style={s.td}>
                      <span style={{
                        ...s.statusBadge,
                        background: lot.is_open ? '#064e3b' : '#4c0519',
                        color: lot.is_open ? C.green : C.red,
                      }}>
                        {lot.is_open ? 'Open' : 'Closed'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
